/**
 * **Which RAW action is this?** — deriving the canonical action id an intent represents, the
 * Fight-vs-Skirmish narrowing an attack-like intent resolves to, whether an in-flight intent
 * matches the verb an earmark named, and the once-per-turn use-legality gate that reads that id.
 */

import type { AttackVerb, EarmarkVerb, EncounterState } from "@lancer-vtt/domain";
import { EARMARK_VERBS, freeActionUses, quickTechOptionKey } from "@lancer-vtt/domain";
import type { EncounterIntent } from "@lancer-vtt/shared";
import { ok, err } from "@lancer-vtt/shared";
import { type LoadedOverlay } from "../effects/loader";
import { SEED_OVERLAY } from "../effects/seed";
import { canStillUseAction } from "./actionAvailability";
import { QUICK_TECH_ACTIVATE_IDS, frozenTechActivation, isProtocolAction } from "./actionShapes";
import { resolveAffordance } from "./carriedActions";
import type { BudgetVerdict } from "./types";

/** Context {@link actionIdForIntent} needs to disambiguate an intent shape that isn't self-describing. */
export type ActionIdCtx = {
  /**
   * Whether this shot is a **mid-attack-lock continuation** rather than the opener — a Barrage
   * continuation, or a Skirmish's Aux-rider shot — the SAME logical action as the opener, not a
   * second use, so it derives no id (recorded once, on the opener/primary only). Ignored for
   * every other intent type.
   */
  midAttack?: boolean;
  /**
   * Whether this `request_attack` is a **follow-up reservation cash-in** (Executioner's Backswing
   * Cut) — a room-derived free action, NOT a second Skirmish use, so it derives no once-per-turn
   * id (the same "free, not a second use" rule {@link midAttack} follows). Ignored for every
   * other intent type.
   */
  followupReservation?: boolean;
  /**
   * The `EncounterState` `actionIdForIntent`'s attack branch reads the attacker's `kind` off,
   * OPTIONAL — mirrors {@link intentCost}'s third `state` param. A `request_attack` (forced or not)
   * from a `"pilot"`-kind attacker derives `"fight"` (`PILOT_ACTIONS`'s own catalog id) instead
   * of `"skirmish"`. Omitted (or the attacker isn't a pilot) keeps the mech-default derivation —
   * never a crash.
   */
  state?: EncounterState;
  /**
   * The loaded overlay {@link actionIdForIntent} consults to tell a Protocol-activation granted
   * action apart from a non-Protocol one (a Protocol's `1/turn` cap must NOT ride `actionUses`).
   * Defaults to the small, hand-built engine-test {@link SEED_OVERLAY} fixture when omitted —
   * **NOT** a room's real loaded overlay. **Every real call site MUST pass its actual loaded
   * overlay explicitly** (`backend/src/encounterRoom.ts`'s pre-dice ladder and `refereeIntent.ts`
   * both do) — only a synthetic engine test may rely on the default.
   */
  overlay?: LoadedOverlay;
};

/**
 * The **once-per-turn RAW action id** `intent` counts as — the sibling of {@link intentCost} that
 * derives which `MECH_ACTIONS`/`NPC_ACTIONS` catalog id (`skirmish`, `ram`, `barrage`,
 * `stabilize`, `quick_tech`, `full_tech`, …) this intent uses, for {@link canUseAction}
 * (`@lancer-vtt/domain`) to gate. Returns `null` when the intent draws on NO once-per-turn id —
 * a reaction (Overwatch; reactions have their own per-round ledger), a mid-attack-lock
 * **continuation** (`ctx.midAttack`; a Barrage continuation or a Skirmish's Aux rider — the same
 * logical use as its opener/primary), a Protocol `request_activate`, or
 * **`request_overcharge`** (Overcharge has its own dedicated {@link checkOverchargeLegality}, so
 * this generic seam stands down rather than re-deriving a competing, less-specific rejection) —
 * so the caller's gate stands down (`ok`), never rejects.
 *
 * The non-obvious derivations:
 *   - `request_attack` (forced or not) is `"skirmish"` — UNLESS `weapon === "act_ram"` (Ram's
 *     synthetic threat-1 melee attack) → `"ram"`, `weapon === "act_grapple"` (Grapple's twin
 *     synthetic threat-1 melee attack) → `"grapple"`, `weapon === "act_improvised_attack"`
 *     (Improvised Attack, RAW Core p.71) → `"improvised_attack"`, or the attack is a TECH attack
 *     through Quick Tech's INVADE route (`tech && weapon === "act_invade"`) → `"quick_tech"`
 *     (INVADE is one of the four Quick Tech options, not a separate catalog action).
 *     **UNLESS `ctx.midAttack`** — a Skirmish's Aux-rider shot derives `null` instead, the same
 *     "same logical action, not a second use" rule Barrage's own continuation follows below.
 *   - `request_barrage` is `"barrage"` **only on the opener** — a continuation shot
 *     (`ctx.midAttack`) is the same logical action, not a second use (the load-bearing Barrage
 *     rule).
 *   - `request_full_tech` is `"full_tech"` **only on the opener** — a continuation pick
 *     (`ctx.midAttack`) is the same logical action, not a second use (mirroring
 *     `request_barrage`'s own rule immediately above).
 *   - `request_activate` against one of the three **activate-routed** Quick Tech built-ins
 *     (`act_scan`, `act_lock_on`, `act_bolster`) collapses to `"quick_tech"` (Bolster/Lock
 *     On/Scan are options of the ONE Quick Tech action, exactly like Invade above); a Protocol
 *     returns `null`; any other `request_activate` returns the carrier's **affordance key**, the
 *     wire id resolved through {@link resolveAffordance} (`decisions/0176` ruling 3), so a bare
 *     (`ms_argonaut_shield`) and a composite (`ms_argonaut_shield:0`) fire of one granted action
 *     name ONE once-per-turn slot. An `ambiguous` bare id, or a call without `ctx.state` (unit
 *     tests only), returns `intent.action` as named.
 *   - `request_stabilize` → `"stabilize"` — one-to-one with the catalog id. `request_overcharge`
 *     → `null` (see above) — it does NOT derive `"overcharge"` here; {@link
 *     checkOverchargeLegality} owns that ledger id exclusively.
 *   - `request_hide`/`end_attack`/`request_disengage` return `null` — not wired to
 *     this ledger (Hide's own gameplay-tier gate is unrelated to the once-per-turn cap;
 *     `end_attack` charges nothing, the action was already charged on the opener; Disengage
 *     needs no once-per-turn id at all — 2 points twice exceeds one turn's action-point ceiling,
 *     which already rules out a double-cast).
 */
export function actionIdForIntent(intent: EncounterIntent, ctx: ActionIdCtx = {}): string | null {
  switch (intent.type) {
    case "request_attack":
      if (intent.reaction) return null; // Overwatch — a reaction, gated by its own per-round ledger.
      // A lock-open Skirmish's Aux-rider continuation is the SAME logical Skirmish as its
      // primary, not a second use — mirrors `request_barrage`'s own `ctx.midAttack` stand-down
      // just below.
      if (ctx.midAttack) return null;
      // Executioner's Backswing Cut cash-in — a room-authorized FREE follow-up, not a second
      // Skirmish use, so it draws on no once-per-turn id (same "free, not a second use" rule as
      // `ctx.midAttack` just above).
      if (ctx.followupReservation) return null;
      return actionIdForAttackLike({
        weapon: intent.weapon,
        tech: intent.tech,
        attackerKind: ctx.state?.combatants[intent.attacker]?.kind,
        techActivation: frozenTechActivation(ctx.state, intent.attacker, intent.weapon),
      });
    case "gm_force":
      // A forced action is still the action it wraps (`decisions/0209`): same ledger id.
      return actionIdForIntent(intent.intent, ctx);
    case "request_barrage":
      return ctx.midAttack ? null : "barrage";
    case "request_stabilize":
      return "stabilize";
    case "request_overcharge":
      // null, NOT "overcharge" — Overcharge's own dedicated `checkOverchargeLegality` (below)
      // owns the once-per-turn rejection message for this ledger id; deriving "overcharge" here
      // would let this generic seam's less-specific message win the race (it runs first at the
      // room's dispatch site). Safe because the once-per-turn WRITE still happens:
      // `grant_overcharge` writes `actionUses.overcharge` directly (a hardcoded reducer branch,
      // not threaded through this actionId mapping), so nulling the read side here doesn't touch
      // the ledger write at all.
      return null;
    case "request_full_tech":
      // Full Tech is `"full_tech"` **only on the opener** — a continuation pick (`ctx.midAttack`)
      // is the same logical action, not a second use (mirrors `request_barrage`'s own rule just
      // above).
      return ctx.midAttack ? null : "full_tech";
    case "request_activate": {
      if (QUICK_TECH_ACTIVATE_IDS.has(intent.action)) return "quick_tech";
      // EVERY non-Protocol granted `request_activate` rides the once-per-turn ledger, regardless
      // of whether (or at what grain) a frequency cap is frozen for it — the affordance key IS the
      // ledger key, and `checkActionUseLegality`'s `staticAllowance` fallback (`actionAllowances`
      // -> the frozen affordance's `cap.max` -> RAW default `1`) is exactly RAW's "a character can't
      // take the same action more than once per turn unless it says otherwise." This wall is
      // INDEPENDENT of a `"scene"`/`"round"`-grain frozen `cap`: a `Limited 3` charge
      // item freezes a scene-wide charge count, but that alone never stops a second throw in the
      // SAME turn while charges remain. Populating this id unconditionally is what closes that
      // gap; the scene/round cap keeps gating the charge count separately (both walls apply,
      // neither substitutes for the other). A `"turn"`-grain cap (rare — most granted actions
      // freeze no cap at all) rides this SAME id too.
      //   EXCEPT a Protocol-activation action: its use must never touch `actionUses` (it would
      //   dirty the turn-virgin window Ordnance/Protocol timing depends on) — it rides the
      //   protocol-safe `protocolUses` sibling instead, gated separately by
      //   `checkGrantedActionLegality` (never through this once-per-turn id).
      const overlay = ctx.overlay ?? SEED_OVERLAY;
      if (isProtocolAction(intent.actor, intent.action, overlay)) return null;
      // The ledger key is the carrier's affordance key, never the wire string: a bare and a
      // composite fire of one granted action are ONE action (RAW Core p.61), so they bill ONE
      // slot. Every reader and both fold arms take this derivation. An `ambiguous` bare id keeps
      // the raw id (`checkGrantedActionLegality` rejects it by name before any fold); an id the
      // carrier does not hold resolves to itself.
      if (!ctx.state) return intent.action;
      const resolved = resolveAffordance({ state: ctx.state, actorId: intent.actor, wireId: intent.action, overlay });
      return resolved.kind === "affordance" ? resolved.affordance.key : intent.action;
    }
    // Pinned ids `act_dismount`/`act_eject` — the SAME ids the `STUNNED_ACTION_EXCEPTIONS` set
    // (`statusLegality.ts`) pins. `request_mount` is early-routed (never reaches
    // `checkActionUseLegality`'s call site in practice) but still derives a real id defensively.
    case "request_dismount":
      return "act_dismount";
    case "request_eject":
      return "act_eject";
    // Reload reaches this generic seam for real (unlike Dismount/Eject/Mount/Jockey above, which
    // are early-routed) — RAW's own "characters can't take the same action more than once per
    // turn" applies to it like any other core verb. Bare `"reload"`, matching `PILOT_ACTIONS`'s
    // own catalog id (mirrors "fight"/"boost", not the `act_*` bridge-action-id convention
    // pinned for Mount/Dismount/Eject).
    case "request_reload":
      return "reload";
    case "request_mount":
      return "act_mount";
    // Pinned ids `act_jockey`/`act_jockey_menu`/`act_shake_off` — all three are early-routed
    // (never reach `checkActionUseLegality`'s call site in practice, see the room's dispatch
    // doc), but derive real ids defensively, mirroring `request_mount` above (a Full action's own
    // 2-point budget already caps it to once per turn, so there is no once-per-turn ledger
    // consumer for these ids today).
    case "request_jockey":
      return "act_jockey";
    case "request_jockey_menu":
      return "act_jockey_menu";
    case "request_shake_off":
      return "act_shake_off";
    // Pinned id `act_ride` — mirrors Jockey's defensive derivation (early-routed, never reaches
    // `checkActionUseLegality`'s call site in practice, see the room's dispatch doc) — a Full
    // action's own 2-point budget already caps it to once per turn.
    case "request_ride":
      return "act_ride";
    // Pinned id `act_ride_dismount` — mirrors Ride's own defensive derivation (early-routed,
    // never reaches `checkActionUseLegality`'s call site in practice) — a Full action's own
    // 2-point budget already caps it to once per turn.
    case "request_ride_dismount":
      return "act_ride_dismount";
    default:
      // `end_attack`, `request_hide`: no once-per-turn id (see doc above).
      return null;
  }
}

/**
 * {@link actionIdForAttackLike}'s args — an object, not positional params, because a fourth
 * positional param (`techActivation`) would trip `CODE_STYLE.md`'s `max-params: 3` oxlint gate.
 * `techActivation` is the caller-derived frozen fact ({@link frozenTechActivation}), mirroring how
 * `attackerKind` is caller-derived rather than this function taking `state` directly — keeps this
 * a pure, state-free derivation.
 */
export type ActionIdForAttackLikeArgs = {
  /** `RequestAttack.weapon` — the named weapon/action id, if any. */
  weapon?: string;
  /** `RequestAttack.tech`. */
  tech?: boolean;
  /** The attacker's `Combatant` `kind` field — `"pilot"` routes to Fight instead of Skirmish. */
  attackerKind?: string;
  /**
   * The frozen tech-attack activation for `weapon` ({@link frozenTechActivation}) — lets a
   * system-granted tech attack (Markerlight: `"full_tech"`) route to its OWN ledger id instead of
   * falling through to Invade's hardcoded `"quick_tech"`. Absent for `act_invade` (never frozen)
   * and any non-tech-attack weapon.
   */
  techActivation?: "quick_tech" | "full_tech";
};

/**
 * Shared `request_attack` (forced or not) → RAW verb id derivation. `attackerKind` is optional —
 * a `"pilot"` attacker's plain weapon attack IS Fight (RAW p.75), a distinct catalog id from the
 * mech's Skirmish, so a pilot and its mech each carry their own once-per-turn ledger entry. Every
 * non-Ram/Grapple/Improvised/Invade attack from a non-pilot (or unknown-kind, e.g. no `state`
 * supplied) derives `"skirmish"`.
 *
 * A tech attack whose `weapon` carries a frozen `techActivation` (a system-granted tech attack,
 * e.g. Markerlight) derives ITS OWN ledger id off that activation (`"full_tech"` ⇒ `"full_tech"`,
 * `"quick_tech"` ⇒ `"quick_tech"`) — checked AFTER the `act_invade` case (which stays hardcoded to
 * `"quick_tech"` and carries no frozen activation).
 *
 * **Exported** so the room can classify a `request_attack` as a genuine loadout Skirmish (id
 * `"skirmish"`) — as opposed to Ram/Grapple/Improvised Attack/Invade/Fight, none of which carry
 * the Aux-rider mechanic — without re-deriving this switch itself.
 *
 * **The return type is deliberately unannotated**: TS infers the literal union from the arms
 * below, and `_attackVerbsMatchProducer` holds it equal to `@lancer-vtt/domain`'s
 * `ATTACK_VERBS` (the attack-verb space authored verbs are validated against). A new arm
 * widens the inferred union and a removed one narrows it; either is a tsc error on the pin
 * until `ATTACK_VERBS` follows, so the authoring vocabulary cannot drift from what attacks
 * actually present as.
 */
export function actionIdForAttackLike(args: ActionIdForAttackLikeArgs) {
  const { weapon, tech, attackerKind, techActivation } = args;
  if (weapon === "act_ram") return "ram";
  if (weapon === "act_grapple") return "grapple";
  // Improvised Attack (RAW Core p.71) gets its own once-per-turn id, same shape as Ram/Grapple.
  if (weapon === "act_improvised_attack") return "improvised_attack";
  if (tech && weapon === "act_invade") return "quick_tech";
  if (tech && techActivation) return techActivation;
  if (attackerKind === "pilot") return "fight";
  return "skirmish";
}

type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;

/**
 * Compile-time pin: {@link actionIdForAttackLike}'s inferred output space IS `AttackVerb`, in
 * both directions. A drift on either side makes this `never` and fails typecheck.
 */
const _attackVerbsMatchProducer: Exact<ReturnType<typeof actionIdForAttackLike>, AttackVerb> = true;

/**
 * **Is `intent` the RAW verb `verb`?** The ONE door from an earmark's verb to an in-flight
 * intent. Nothing else in the engine may re-derive this mapping — a second copy is how the two
 * id spaces drift back together.
 *
 * **Why this is not `actionIdForIntent(intent) === verb`, which is the tempting one-liner and is
 * WRONG.** That function derives the once-per-turn **ledger** id, and the ledger deliberately
 * COLLAPSES Scan / Lock On / Bolster into a single `"quick_tech"`
 * ({@link QUICK_TECH_ACTIVATE_IDS}). An earmark for a free **Lock On** matched through the
 * ledger would therefore also match an **Invade** or a **Scan** — handing out a free action the
 * carrier's RAW never granted, which is the exact over-grant this whole primitive exists to
 * remove. So the activate family below matches the **pre-collapse** `intent.action` id directly.
 *
 * **The trap worth stating plainly: Boost is the one verb where the earmark name and the ledger
 * id coincide**, so the flagship carrier (Everest's Hyperspec Fuel Injector) cannot reveal this
 * bug — a one-carrier implementation ships it green. That is why `"lock_on"` is pinned here by
 * test rather than waiting on an authored Lock On carrier.
 *
 * **`"boost"` never matches ANY intent, and that is correct, not a gap.** Boost is not an
 * intent at all — it is a domain `boost` *command* that bypasses `intentCost` entirely and
 * self-enforces inside the pure reducer. Its earmark is read and consumed in
 * `packages/domain`'s `boost` reducer case. A reader who "fixes" this by adding a Boost arm here
 * would be adding a second, dead consumption site.
 *
 * The attack family reuses the shipped {@link actionIdForAttackLike} derivation rather than
 * re-deriving weapon ids: its output space (`ram`/`grapple`/`skirmish`/`fight`/`improvised_
 * attack`/`quick_tech`/`full_tech`) collapses only Invade→`quick_tech`, and no attack-reachable
 * collapsed id is a member of `EARMARK_VERBS`, so no attack can match a tech verb through it.
 */
export function matchesEarmarkedVerb(verb: EarmarkVerb, intent: EncounterIntent, ctx: ActionIdCtx = {}): boolean {
  switch (intent.type) {
    case "request_activate":
      // PRE-COLLAPSE, by construction — never `actionIdForIntent`. See this function's doc.
      if (intent.action === "act_lock_on") return verb === "lock_on";
      if (intent.action === "act_scan") return verb === "scan";
      if (intent.action === "act_bolster") return verb === "bolster";
      return false;
    case "request_attack":
      // An Overwatch reaction is not the verb's ordinary use; it spends the reaction slot.
      if (intent.reaction) return false;
      return (
        actionIdForAttackLike({
          weapon: intent.weapon,
          tech: intent.tech,
          attackerKind: ctx.state?.combatants[intent.attacker]?.kind,
          techActivation: frozenTechActivation(ctx.state, intent.attacker, intent.weapon),
        }) === verb
      );
    case "request_stabilize":
      return verb === "stabilize";
    case "request_mount":
      return verb === "mount";
    case "request_dismount":
      return verb === "dismount";
    default:
      // Every other intent is outside the earmarkable verb vocabulary. Defaulting to NO match is
      // the safe direction: an unmatched earmark simply goes unspent (the carrier keeps it until
      // its turn-start sweep), whereas a loose match hands out a free action RAW never granted.
      return false;
  }
}

/** The RAW earmark verb `intent` is, if any (`ActionEvent.verb`), read through {@link matchesEarmarkedVerb}. */
export function earmarkVerbOf(intent: EncounterIntent, ctx: ActionIdCtx = {}): EarmarkVerb | undefined {
  return EARMARK_VERBS.find((v) => matchesEarmarkedVerb(v, intent, ctx));
}

/**
 * The RAW verb whose earmark `intent`'s actor currently HOLDS and which `intent` matches, if
 * any. Reads `current.resources` through {@link freeActionUses} — never peeks at the namespaced
 * key by hand.
 *
 * Scans {@link EARMARK_VERBS} rather than deriving a verb from the intent, so the matcher stays
 * the single door: this asks each held earmark "are you this intent?", which is the same
 * question {@link matchesEarmarkedVerb} answers, instead of inventing an intent→verb inverse
 * that could disagree with it.
 */
export function earmarkedVerbHeld(
  intent: EncounterIntent,
  state: EncounterState | undefined,
  actor: string,
): EarmarkVerb | undefined {
  if (state === undefined) return undefined;
  const resources = state.combatants[actor]?.current.resources;
  if (resources === undefined) return undefined;
  for (const verb of EARMARK_VERBS) {
    if (freeActionUses(resources, verb) <= 0) continue;
    if (matchesEarmarkedVerb(verb, intent, { state })) return verb;
  }
  return undefined;
}

/**
 * May `actor` still take the **once-per-turn RAW action** `intent` derives? The room-facing
 * counterpart of {@link canUseAction} (`@lancer-vtt/domain`) — called **alongside**
 * {@link checkBudget}, before any dice, for every core-verb resolution intent
 * {@link actionIdForIntent} derives a non-`null` id for. Mirrors `checkBudget`'s inert posture: an
 * idle state (no turn sequenced) is allowed, since enforcement only governs a running fight.
 * **Boost and Overcharge are NOT gated here** — Boost self-enforces inside the pure domain
 * reducer (its reducer case calls `canUseAction` directly), and Overcharge is un-gated here
 * because `actionIdForIntent` maps `request_overcharge` to `null` — it keeps its own dedicated
 * {@link checkOverchargeLegality} (which reads the SAME `actionUses` ledger this helper would
 * otherwise read, alongside its mech-only / on-turn walls), so gating it a second time here
 * would just re-derive the identical verdict through a generic, less specific error message. An
 * intent `actionIdForIntent` maps to `null` (a reaction, a Barrage continuation, a Protocol
 * activate, or Overcharge) is un-gated (`ok`). A granted activate is gated on its affordance key,
 * whichever id shape the caller named.
 *
 * Returns a verdict the room surfaces (a rejection is a logged no-op before any dice).
 */
export type CheckActionUseLegalityArgs = {
  state: EncounterState;
  intent: EncounterIntent;
  /** The combatant whose ledger to check (the resolution source — `intentCost`'s `.actor`). */
  actor: string;
} & ActionIdCtx;

export function checkActionUseLegality(args: CheckActionUseLegalityArgs): BudgetVerdict {
  const { state, intent, actor, ...ctx } = args;
  const turn = state.activation.turn;
  if (turn.phase !== "acting") return ok(undefined);
  const id = actionIdForIntent(intent, { ...ctx, state });
  if (id === null) return ok(undefined);
  const optionKey = quickTechOptionLedgerId(intent, id);
  if (canStillUseAction({ state, actor, id, ...(optionKey !== undefined ? { countKey: optionKey } : {}) })) return ok(undefined);
  const name = state.combatants[actor]?.name ?? actor;
  if (optionKey !== undefined) {
    return err(`${id}: ${name} has already used this Quick Tech option this turn (a different option must be chosen each time — Core p.69)`);
  }
  return err(`${id}: ${name} has already used this action this turn (once per turn)`);
}

/**
 * The **per-option** once-per-turn slot a Quick Tech use counts against, or `undefined` when
 * `actionId` (the intent's {@link actionIdForIntent} derivation) is not `"quick_tech"`.
 *
 * RAW Core p.69: "Unlike other quick actions, QUICK TECH can be taken more than once per turn;
 * however, a different option must be chosen every time." So `"quick_tech"` keeps naming the
 * action (the earmark, prepare, allowance and the menu read it), while the repeat wall counts
 * `quick_tech:<option>`: `act_scan`/`act_lock_on`/`act_bolster` for the activate routes, the
 * tech-attack weapon for Invade (`act_invade`; every Invade sub-option is the one INVADE option)
 * and for a system-granted Quick Tech attack (its frozen `techAttackActions` key). The referee
 * stamps this slot beside `"quick_tech"` in the same batch, and {@link checkActionUseLegality}
 * reads it.
 */
export function quickTechOptionLedgerId(intent: EncounterIntent, actionId: string | null): string | undefined {
  if (actionId !== "quick_tech") return undefined;
  switch (intent.type) {
    case "request_activate":
      return quickTechOptionKey(intent.action);
    case "request_attack":
      return quickTechOptionKey(intent.weapon ?? "act_invade");
    case "gm_force":
      return quickTechOptionLedgerId(intent.intent, actionId);
    default:
      return undefined;
  }
}
